import React,{useState} from 'react';
import {useParams,Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {getPosesByCategory,categories} from '../data/poses';

const {FiArrowLeft,FiImage,FiEye,FiCheckSquare,FiSquare,FiCamera}=FiIcons;

// Color mapping for categories
const categoryBackgroundColors={
  business: '#0F0F0F',
  senior: '#1C1C1C', 
  couple: '#2E2A2B',
  creative: '#3B2F2F',
  lifestyle: '#4B3B3F',
  fitness: '#5C4B51',
  artistic: '#6D5C5F',
  branding: '#7E6D6F'
};

function CategoryView() {
  const {categoryId}=useParams();
  const poses=getPosesByCategory(categoryId);
  const category=categories.find(cat=> cat.id===categoryId);
  const [selectedPoses,setSelectedPoses]=React.useState(()=>{
    return JSON.parse(localStorage.getItem('selectedPoses') || '[]');
  });

  if (!category) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold text-slate-800 mb-4">Category not found</h2>
        <Link to="/" className="text-brand-primary hover:text-brand-primary-dark">
          Return to home
        </Link>
      </div>
    );
  }

  const togglePoseSelection=(poseId)=> {
    let newSelections;
    if (selectedPoses.includes(poseId)) {
      newSelections=selectedPoses.filter(id=> id !==poseId);
    } else {
      newSelections=[...selectedPoses,poseId];
    }
    setSelectedPoses(newSelections);
    localStorage.setItem('selectedPoses',JSON.stringify(newSelections));
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  const clearSelection=()=> {
    const categoryPoses=poses.map(p=> p.id);
    const newSelections=selectedPoses.filter(id=> !categoryPoses.includes(id));
    setSelectedPoses(newSelections);
    localStorage.setItem('selectedPoses',JSON.stringify(newSelections));
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  const selectAllInCategory=()=> {
    const categoryPoses=poses.map(p=> p.id);
    const newSelections=[...new Set([...selectedPoses,...categoryPoses])];
    setSelectedPoses(newSelections);
    localStorage.setItem('selectedPoses',JSON.stringify(newSelections));
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('favoritesUpdated'));
  };

  const getPoseNumber=(poseId)=> {
    const parts=poseId.split('-');
    return parts[1] ? parseInt(parts[1]) : 1;
  };

  const selectedInCategory=selectedPoses.filter(id=> id.startsWith(categoryId.toUpperCase().substring(0,3))).length;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <motion.div initial={{y: -20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.6}} className="mb-8" >
        <div className="flex items-center justify-between mb-6">
          <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-800 transition-colors text-sm">
            <SafeIcon icon={FiArrowLeft} className="mr-2" />
            Back to Categories
          </Link>
          <h1 className="text-lg font-bold text-slate-800">{category.name} Poses</h1>
          {selectedInCategory > 0 && (
            <div className="flex items-center gap-4">
              <span className="text-xs font-medium text-slate-600">
                {selectedInCategory} pose{selectedInCategory !==1 ? 's' : ''} selected
              </span>
              {selectedInCategory < poses.length && (
                <button 
                  onClick={selectAllInCategory} 
                  className="text-xs text-brand-primary hover:text-brand-primary-dark" 
                >
                  Select all
                </button>
              )}
              <button 
                onClick={clearSelection} 
                className="text-xs text-brand-accent hover:text-brand-accent-dark" 
              >
                Clear selection
              </button>
              <Link 
                to="/selection" 
                className="bg-brand-primary hover:bg-brand-primary-dark text-white px-3 py-1 rounded text-xs font-medium transition-colors" 
              >
                View Selection
              </Link>
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <p className="text-sm text-slate-600 mb-4">{category.description}</p>
          <div className="flex items-center text-slate-500">
            <SafeIcon icon={FiImage} className="mr-2 text-sm" />
            <span className="text-sm">{poses.length} poses available</span>
          </div>
        </div>
      </motion.div>

      {/* Poses Grid */}
      <motion.div initial={{y: 20,opacity: 0}} animate={{y: 0,opacity: 1}} transition={{duration: 0.6,delay: 0.2}} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" >
        {poses.map((pose,index)=> (
          <motion.div 
            key={pose.id} 
            initial={{y: 20,opacity: 0}} 
            animate={{y: 0,opacity: 1}} 
            transition={{duration: 0.5,delay: index * 0.05}} 
            whileHover={{y: -3,scale: 1.02}} 
            whileTap={{scale: 0.98}} 
            className="relative" 
          >
            <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
              {/* Selection Checkbox */}
              <button 
                onClick={()=> togglePoseSelection(pose.id)} 
                className="absolute top-3 right-3 z-10 p-2 bg-white/90 rounded-full shadow-md" 
              >
                <SafeIcon 
                  icon={selectedPoses.includes(pose.id) ? FiCheckSquare : FiSquare} 
                  className={`text-sm ${selectedPoses.includes(pose.id) ? 'text-brand-primary' : 'text-slate-600'}`} 
                />
              </button>

              <Link 
                to={`/pose/${pose.id}`} 
                state={{fromCategory: true}}
                className="block"
              >
                <div className="aspect-square relative overflow-hidden" style={{backgroundColor: categoryBackgroundColors[category.id]}}>
                  <img 
                    src={pose.link} 
                    alt={`${category.name} Pose ${getPoseNumber(pose.id)}`} 
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" 
                    onError={(e)=> {
                      e.target.style.display='none';
                      e.target.nextSibling.style.display='flex';
                    }} 
                  />
                  <div style={{display: 'none'}} className="w-full h-full flex items-center justify-center">
                    <SafeIcon icon={FiImage} className="text-4xl text-white/50" />
                  </div>

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 hover:opacity-100">
                    <div className="bg-white/90 rounded-full p-3">
                      <SafeIcon icon={FiEye} className="text-lg text-slate-800" />
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="font-bold text-slate-800 mb-2 text-sm">
                    {category.name} Pose {getPoseNumber(pose.id)}
                  </h3>
                  <p className="text-xs text-slate-600 line-clamp-2">
                    {pose.instruction.substring(0,80)}...
                  </p>
                </div>
              </Link>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {poses.length===0 && (
        <div className="text-center py-20">
          <SafeIcon icon={FiImage} className="text-6xl text-slate-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-800 mb-2">No poses found</h2>
          <p className="text-slate-600">This category doesn't have any poses yet.</p>
        </div>
      )}
    </div>
  );
}

export default CategoryView;